import React from "react";
import PropTypes from "prop-types";
import { useParams, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { colors } from "../theme";

const Container = styled.nav`
  margin: 22px 0 57px;
  text-align: center;
  text-transform: uppercase;

  @media (max-width: 767px) {
    margin: 0 0 30px;
  }

  a {
    display: inline-block;
    text-decoration: none;
  }

  > span {
    display: inline-block;
    font-size: 1.7rem;
    margin: 0 8px;
    width: 24px;

    @media (max-width: 767px) {
      margin: 0 6px;
    }
  }
`;

const Arrow = styled(Link)`
  border: 1px solid transparent;
  color: ${colors.primaryColor};
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 0.04rem;
  margin: 0 22px;
  padding: 9px 0;
  transition: 0.3s;

  :hover {
    border-color: ${colors.primaryColor};
    padding: 9px 15px;
  }

  i {
    margin: 0 4px;
  }

  @media (max-width: 767px) {
    margin: 0 6px;

    span {
      display: none;
    }
  }
`;

const Current = styled.span`
  border-bottom: 3px solid ${colors.primaryColor};
  color: ${colors.primaryColor};
  padding: 8px 0;
`;

const Page = styled(Link)`
  border-bottom: 3px solid transparent;
  color: ${colors.gray};
  font-size: 1.7rem;
  margin: 0 8px;
  padding: 8px 0;
  transition: 0.3s;
  width: 24px;

  @media (max-width: 767px) {
    margin: 0 6px;
  }

  :hover {
    border-color: ${colors.primaryColor};
    color: ${colors.primaryColor};
  }
`;

const Ellipsis = styled.span`
  color: ${colors.gray};
`;

function Pagination({ nbrPages }) {
  const { pathname } = useLocation();
  const params = useParams();

  if (nbrPages === 1) return false;

  const currentPage = parseInt(params.page, 10) || 1;
  const baseUrl = pathname.replace(/\/page\/\d+/, "") + "/page/";

  function getPages() {
    if (nbrPages <= 5) {
      const pages = [];

      for (let i = 1; i <= nbrPages; i++) {
        if (i === currentPage) {
          pages.push(<Current key={i}>{i}</Current>);
        } else {
          pages.push(
            <Page key={i} to={`${baseUrl}${i}`}>
              {i}
            </Page>
          );
        }
      }

      return pages;
    }

    if (currentPage < 5) {
      let pages = [];

      for (let i = 1; i < 5; i++) {
        if (i === currentPage) {
          pages.push(<Current key={i}>{i}</Current>);
        } else {
          pages.push(
            <Page key={i} to={`${baseUrl}${i}`}>
              {i}
            </Page>
          );
        }
      }

      pages = [
        ...pages,
        <Ellipsis key={5}>...</Ellipsis>,
        <Page key={6} to={`${baseUrl}${nbrPages}`}>
          {nbrPages}
        </Page>
      ];

      return pages;
    }

    if (currentPage + 3 < nbrPages) {
      return (
        <>
          <Page to={`${baseUrl}1`}>1</Page>
          <Ellipsis>...</Ellipsis>
          <Page to={`${baseUrl}${currentPage - 1}`}>{currentPage - 1}</Page>
          <Current to={`${baseUrl}${currentPage}`}>{currentPage}</Current>
          <Page to={`${baseUrl}${currentPage + 1}`}>{currentPage + 1}</Page>
          <Ellipsis>...</Ellipsis>
          <Page to={`${baseUrl}${nbrPages}`}>{nbrPages}</Page>
        </>
      );
    }

    const pages = [
      <Page key={1} to={`${baseUrl}1`}>
        1
      </Page>,
      <Ellipsis key={2}>...</Ellipsis>
    ];

    for (let i = nbrPages - 3; i <= nbrPages; i++) {
      if (i === currentPage) {
        pages.push(<Current key={i}>{i}</Current>);
      } else {
        pages.push(
          <Page key={i} to={`${baseUrl}${i}`}>
            {i}
          </Page>
        );
      }
    }

    return pages;
  }

  return (
    <Container role="navigation" aria-label="Pagination">
      {currentPage !== 1 && (
        <Arrow to={`${baseUrl}${currentPage - 1}`}>
          <span>Page préc.</span> <i className="far fa-chevron-left" />
        </Arrow>
      )}

      {getPages()}

      {currentPage !== nbrPages && (
        <Arrow to={`${baseUrl}${currentPage + 1}`}>
          <i className="far fa-chevron-right" /> <span>Page suiv.</span>
        </Arrow>
      )}
    </Container>
  );
}

Pagination.propTypes = {
  nbrPages: PropTypes.number.isRequired
};

export default Pagination;
